import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { awsConfig } from "../aws-config";

interface PricingItem {
  category: string;
  jobSize: string;
  price: number;
  description: string;
}

const CATEGORY_ICONS: Record<string, string> = {
  "Electrical": "⚡",
  "Plumbing": "🔧",
  "Cleaning": "🧹",
  "Outdoor": "🌿",
  "Moving": "📦",
  "Painting": "🎨",
  "Carpentry": "🪚",
  "Interior Design": "🛋️",
};

export default function LeadPricingPage() {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState<PricingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");

  useEffect(() => {
    axios.get(`${awsConfig.apiUrl}/lead-pricing`)
      .then(res => setPricing(res.data.pricing || []))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  const categories = ["All", ...Array.from(new Set(pricing.map(p => p.category))).sort()];
  const filtered = activeCategory === "All" ? pricing : pricing.filter(p => p.category === activeCategory);
  const grouped = filtered.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {} as Record<string, PricingItem[]>);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white px-5 pt-4 pb-5 shadow-sm">
        <button onClick={() => navigate(-1)} className="text-gray-400 text-sm mb-3 block">← Back</button>
        <h1 className="font-display text-2xl font-bold text-navy">Lead Pricing</h1>
        <p className="text-xs text-gray-400 mt-1">Transparent pricing for every service category</p>
      </div>

      {/* Free trial banner */}
      <div className="mx-5 mt-4 bg-gradient-to-r from-teal to-green-600 rounded-2xl p-4 text-white">
        <div className="font-bold text-sm mb-1">🎉 New Pro? First 3 leads are FREE!</div>
        <div className="text-xs opacity-90">No credit card required to get started. Start winning jobs today.</div>
      </div>

      {/* Category filter */}
      <div className="px-5 mt-4 overflow-x-auto">
        <div className="flex gap-2 pb-2">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={"px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-all " +
                (activeCategory === cat
                  ? "bg-mouna-blue text-white"
                  : "bg-white text-gray-500 border border-gray-200")}
            >
              {cat === "All" ? "All Services" : `${CATEGORY_ICONS[cat] || ""} ${cat}`}
            </button>
          ))}
        </div>
      </div>

      <div className="px-5 pt-4 pb-24">
        {loading && (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-mouna-blue border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {!loading && Object.entries(grouped).sort().map(([category, items]) => (
          <div key={category} className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl">{CATEGORY_ICONS[category] || "🔨"}</span>
              <h2 className="font-bold text-navy">{category}</h2>
            </div>
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
              {items.sort((a, b) => a.price - b.price).map((item, i) => (
                <div key={item.jobSize}
                  className={"flex items-center justify-between px-4 py-3 " +
                    (i < items.length - 1 ? "border-b border-gray-100" : "")}>
                  <div>
                    <div className="font-semibold text-navy text-sm">{item.jobSize}</div>
                    <div className="text-xs text-gray-400">{item.description}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-mouna-blue text-lg">${item.price}</div>
                    <div className="text-xs text-gray-400">per lead</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="bg-blue-pale rounded-2xl p-4 mt-2">
          <div className="font-bold text-navy text-sm mb-1">How lead pricing works</div>
          <div className="text-xs text-gray-500 space-y-1">
            <div>• Client requests a service and selects job size</div>
            <div>• Mouna matches 3 best pros for the job</div>
            <div>• Client picks their preferred pro</div>
            <div>• Selected pro is charged the lead fee</div>
            <div>• First 3 leads are always free for new pros</div>
          </div>
        </div>
      </div>
    </div>
  );
}
